/**
 * PagedDocument — measures section heights and distributes them across
 * multiple <Paper> pages. Each page is a real 8.5in × 11in letter sheet
 * with the OJB wide-left-margin layout (logo + office ribbon).
 *
 * Why not CSS print media queries:
 *   - We want visual pagination on-screen during editing (not just when printing)
 *   - Each page needs the floated logo + ribbon in the margin strip, which
 *     requires a fresh <Paper> wrapper per page
 *
 * Measurement strategy:
 *   1. Render each section read-only in a hidden off-screen container sized
 *      to the content column width (5in)
 *   2. useLayoutEffect reads offsetHeight from each measurement node
 *   3. Greedy-pack sections into pages by accumulating height until the
 *      content area fills (8.49in); start a new page on overflow
 *   4. Actual visible rendering uses the editable variant of each section
 *
 * The hidden measurement container uses `inert` so React's contentEditable
 * nodes inside it don't receive focus or events.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Paper } from './Paper.tsx';

/** Decorator rendered between sheet N and N+1. Perforation lines on
 *  each side of a centered "Page X of Y" pill — keeps the paginated
 *  feel without competing with the paper itself. Preview-only;
 *  doesn't render in the exported .docx since it's a separate JSX
 *  element outside any Paper. */
function PageBreakDecoration({
  pageNumber,
  totalPages,
}: {
  pageNumber: number;
  totalPages: number;
}) {
  return (
    <div
      aria-hidden
      className="my-3 flex select-none items-center justify-center gap-3 px-12"
    >
      <div
        className="h-px flex-1"
        style={{
          // Dotted perforation feel — pine-tinted, low opacity so it
          // reads as a soft fold-mark instead of a rule.
          backgroundImage:
            'repeating-linear-gradient(to right, rgba(55,85,66,0.35) 0 4px, transparent 4px 8px)',
        }}
      />
      <span className="rounded-full border border-pine/30 bg-cloud/80 px-3 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-pine/80 shadow-sm backdrop-blur">
        Page {pageNumber} / {totalPages}
      </span>
      <div
        className="h-px flex-1"
        style={{
          backgroundImage:
            'repeating-linear-gradient(to right, rgba(55,85,66,0.35) 0 4px, transparent 4px 8px)',
        }}
      />
    </div>
  );
}

export interface DocumentSection {
  key: string;
  /** Read-only render used for height measurement */
  renderMeasure: () => ReactNode;
  /** Interactive render used in the actual displayed pages */
  renderDisplay: () => ReactNode;
}

interface Props {
  sections: DocumentSection[];
  /** Whether the bottom-right page number is rendered on each
   *  sheet. Default true. Affects ONLY the on-screen preview;
   *  export honors the same flag via the meeting's payload. */
  showPageNumbers?: boolean;
}

// Content column height = 11in - 1in (top margin) - 1.51in (bottom margin) = 8.49in
// At 96dpi, 8.49in ≈ 815px. Subtract a small buffer for rounding + section
// gap accumulation.
const CONTENT_AREA_HEIGHT_PX = 815;
const SECTION_GAP_PX = 20;

export function PagedDocument({ sections, showPageNumbers = true }: Props) {
  const measureRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [pageAssignments, setPageAssignments] = useState<number[][]>([]);

  useLayoutEffect(() => {
    const heights = measureRefs.current.map((el) => el?.offsetHeight ?? 0);

    const pages: number[][] = [];
    let currentPage: number[] = [];
    let currentHeight = 0;

    sections.forEach((_, i) => {
      const sectionHeight = heights[i] ?? 0;
      const gap = currentPage.length > 0 ? SECTION_GAP_PX : 0;
      const projected = currentHeight + gap + sectionHeight;

      if (projected > CONTENT_AREA_HEIGHT_PX && currentPage.length > 0) {
        // Current page is full — push and start a new one
        pages.push(currentPage);
        currentPage = [i];
        currentHeight = sectionHeight;
      } else {
        currentPage.push(i);
        currentHeight = projected;
      }
    });

    if (currentPage.length > 0) pages.push(currentPage);

    // Fallback: if we somehow ended up with zero pages (shouldn't happen)
    if (pages.length === 0 && sections.length > 0) {
      pages.push(sections.map((_, i) => i));
    }

    setPageAssignments(pages);
  }, [sections]);

  // Before layout effect runs, render all sections on page 1 so there's something visible
  const effectiveAssignments =
    pageAssignments.length > 0
      ? pageAssignments
      : [sections.map((_, i) => i)];

  return (
    <>
      {/* Hidden measurement layer. Positioned fixed + offscreen + invisible +
          no pointer events, so it doesn't affect layout, paint, or user input
          even though it renders read-only copies of every section for height
          measurement. React's contentEditable concerns are sidestepped because
          renderMeasure always passes readOnly=true (no editable spans). */}
      <div
        aria-hidden
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: -1,
          width: '5in',
          padding: 0,
          margin: 0,
        }}
      >
        {sections.map((s, i) => (
          <div
            key={s.key}
            ref={(el) => {
              measureRefs.current[i] = el;
            }}
            style={{ marginBottom: `${SECTION_GAP_PX}px` }}
          >
            {s.renderMeasure()}
          </div>
        ))}
      </div>

      {/* Actual visible pages — interleaved with a "page break"
          decorator between sheets. The decorator combines:
          - A perforation/dotted rule on each side of the gap
          - A "page X of Y" pill centered between pages
          The whole thing reinforces the paginated-document feel and
          gives admins a clearer scanning anchor than the previous
          plain 24px gap. */}
      <div>
        {effectiveAssignments.map((indices, pageIndex) => (
          <div key={pageIndex}>
            <Paper
              pageNumber={pageIndex + 1}
              totalPages={effectiveAssignments.length}
              showPageNumber={showPageNumbers}
            >
              <div
                className="flex h-full flex-col"
                style={{ gap: `${SECTION_GAP_PX}px` }}
              >
                {indices.map((si) => (
                  <div key={sections[si].key}>{sections[si].renderDisplay()}</div>
                ))}
              </div>
            </Paper>
            {pageIndex < effectiveAssignments.length - 1 && (
              <PageBreakDecoration
                pageNumber={pageIndex + 1}
                totalPages={effectiveAssignments.length}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
}
